import React, { useCallback, useEffect, useState } from 'react';
import { Cadence, CostCategory, Currency, FixedCost, financeApi } from '../../api/finance';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { Spinner } from '../../components/ui/Spinner';
import { useToast } from '../../hooks/useToast';
import { fieldLabel, isoDay, longDate, muted, price, sectionNote, sectionTitle, select, td, th, thRight, tdRight } from './shared';

export const CATEGORIES: Record<CostCategory, string> = {
  servers: 'Servers and hosting',
  software: 'Software and subscriptions',
  staff: 'Staff',
  office: 'Office',
  marketing: 'Marketing',
  compliance: 'Legal and compliance',
  other: 'Other',
};

const CADENCES: Record<Cadence, string> = {
  monthly: 'Every month',
  yearly: 'Every year',
  once: 'Once',
};

/**
 * Costs nothing in the system records: a server bill, a software plan, rent.
 * Monthly and yearly ones run until they are given an end date.
 */
export const FixedCostsPanel: React.FC<{ canManage: boolean }> = ({ canManage }) => {
  const toast = useToast();
  const [costs, setCosts] = useState<FixedCost[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<FixedCost | 'new' | null>(null);
  const [removing, setRemoving] = useState<FixedCost | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setError(null);
      setCosts(await financeApi.fixedCosts());
    } catch {
      setError('Could not load fixed costs.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function remove() {
    if (!removing) return;
    setBusy(true);
    try {
      await financeApi.removeFixedCost(removing.id);
      toast.success(`${removing.name} removed.`);
      setRemoving(null);
      await load();
    } catch (err) {
      toast.error(err, 'Could not remove the cost.');
    } finally {
      setBusy(false);
    }
  }

  const today = isoDay(new Date());

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
        <div>
          <h3 style={sectionTitle}>Fixed costs</h3>
          <p style={sectionNote}>
            Servers, software, rent and anything else PayIQ pays on a schedule. A monthly cost is spread over its
            month's days, so a report for half a month counts half of it. To stop a cost, give it an end date rather
            than removing it, so earlier months keep it.
          </p>
        </div>
        {canManage && <Button onClick={() => setEditing('new')}>Add a cost</Button>}
      </div>

      {error && <div style={{ color: 'var(--danger-900)', fontSize: 13 }}>{error}</div>}
      {!costs && !error && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <Spinner />
        </div>
      )}

      {costs && (
        <Card padding={0}>
          {costs.length === 0 ? (
            <p style={{ padding: 24, margin: 0, fontSize: 14, color: 'var(--neutral-600)' }}>
              No fixed costs yet. Add your server bill first: it is usually the biggest.
            </p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
                <thead>
                  <tr>
                    <th style={th}>Cost</th>
                    <th style={th}>Kind</th>
                    <th style={thRight}>Amount</th>
                    <th style={th}>Runs</th>
                    <th style={th} aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {costs.map((c) => {
                    const ended = c.endsOn && c.endsOn < today;
                    return (
                      <tr key={c.id} style={{ opacity: ended ? 0.6 : 1 }}>
                        <td style={td}>
                          <div>{c.name}</div>
                          {(c.vendor || c.note) && (
                            <div style={muted}>{[c.vendor, c.note].filter(Boolean).join(' · ')}</div>
                          )}
                        </td>
                        <td style={td}>{CATEGORIES[c.category] ?? c.category}</td>
                        <td style={tdRight}>
                          {price(c.amount, c.currency)}
                          <div style={muted}>{CADENCES[c.cadence]}</div>
                        </td>
                        <td style={td}>
                          {c.cadence === 'once'
                            ? longDate(c.startsOn)
                            : `${longDate(c.startsOn)} to ${c.endsOn ? longDate(c.endsOn) : 'now'}`}
                        </td>
                        <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                          {canManage && (
                            <div style={{ display: 'inline-flex', gap: 8 }}>
                              <Button size="sm" variant="secondary" onClick={() => setEditing(c)}>
                                Edit
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => setRemoving(c)}>
                                Remove
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {editing && (
        <CostForm
          cost={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await load();
          }}
        />
      )}

      <ConfirmDialog
        isOpen={!!removing}
        title="Remove this cost?"
        message={
          removing
            ? `${removing.name} will disappear from every report, past months included. If it has only stopped, edit it and give it an end date instead.`
            : ''
        }
        confirmLabel="Remove"
        variant="danger"
        loading={busy}
        onConfirm={() => void remove()}
        onCancel={() => setRemoving(null)}
      />
    </div>
  );
};

const CostForm: React.FC<{
  cost: FixedCost | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
}> = ({ cost, onClose, onSaved }) => {
  const toast = useToast();
  const [name, setName] = useState(cost?.name ?? '');
  const [vendor, setVendor] = useState(cost?.vendor ?? '');
  const [category, setCategory] = useState<CostCategory>(cost?.category ?? 'servers');
  const [amount, setAmount] = useState(cost ? String(cost.amount) : '');
  const [currency, setCurrency] = useState<Currency>(cost?.currency ?? 'NGN');
  const [cadence, setCadence] = useState<Cadence>(cost?.cadence ?? 'monthly');
  const [startsOn, setStartsOn] = useState(cost?.startsOn ?? isoDay(new Date()));
  const [endsOn, setEndsOn] = useState(cost?.endsOn ?? '');
  const [note, setNote] = useState(cost?.note ?? '');
  const [saving, setSaving] = useState(false);
  const [tried, setTried] = useState(false);

  const value = Number(amount);
  const errors = {
    name: name.trim() ? undefined : 'Give it a name.',
    amount: amount.trim() !== '' && value >= 0 ? undefined : 'Enter an amount of 0 or more.',
    startsOn: startsOn ? undefined : 'Pick a date.',
    endsOn: endsOn && endsOn < startsOn ? 'It cannot end before it starts.' : undefined,
  };
  const valid = Object.values(errors).every((e) => !e);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setTried(true);
    if (!valid) return;
    setSaving(true);
    const body = {
      name: name.trim(),
      vendor: vendor.trim() || null,
      category,
      amount: Math.round(value * 100) / 100,
      currency,
      cadence,
      startsOn,
      endsOn: cadence === 'once' ? null : endsOn || null,
      note: note.trim() || null,
    };
    try {
      if (cost) await financeApi.updateFixedCost(cost.id, body);
      else await financeApi.createFixedCost(body);
      toast.success(cost ? `${body.name} saved.` : `${body.name} added.`);
      await onSaved();
    } catch (err) {
      toast.error(err, 'Could not save the cost.');
    } finally {
      setSaving(false);
    }
  }

  const show = (k: keyof typeof errors) => (tried ? errors[k] : undefined);

  return (
    <Modal isOpen onClose={onClose} title={cost ? `Edit ${cost.name}` : 'Add a fixed cost'} maxWidth={560}>
      <form onSubmit={save} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Input
          label="What it is"
          placeholder="e.g. API servers"
          maxLength={120}
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={show('name')}
          autoFocus
        />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Input
            label="Paid to (optional)"
            placeholder="e.g. AWS"
            maxLength={80}
            value={vendor}
            onChange={(e) => setVendor(e.target.value)}
          />
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={fieldLabel}>Kind</span>
            <select value={category} onChange={(e) => setCategory(e.target.value as CostCategory)} style={select}>
              {(Object.keys(CATEGORIES) as CostCategory[]).map((k) => (
                <option key={k} value={k}>
                  {CATEGORIES[k]}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px 150px', gap: 12, alignItems: 'end' }}>
          <Input
            label="Amount"
            type="number"
            inputMode="decimal"
            min={0}
            step="any"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            error={show('amount')}
          />
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={fieldLabel}>Currency</span>
            <select value={currency} onChange={(e) => setCurrency(e.target.value as Currency)} style={select}>
              <option value="NGN">Naira</option>
              <option value="USD">Dollars</option>
            </select>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={fieldLabel}>Paid</span>
            <select value={cadence} onChange={(e) => setCadence(e.target.value as Cadence)} style={select}>
              {(Object.keys(CADENCES) as Cadence[]).map((k) => (
                <option key={k} value={k}>
                  {CADENCES[k]}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Input
            label={cadence === 'once' ? 'Paid on' : 'Starts on'}
            type="date"
            value={startsOn}
            onChange={(e) => setStartsOn(e.target.value)}
            error={show('startsOn')}
          />
          {cadence !== 'once' && (
            <Input
              label="Ends on (optional)"
              type="date"
              value={endsOn}
              min={startsOn}
              onChange={(e) => setEndsOn(e.target.value)}
              error={show('endsOn') ?? errors.endsOn}
              hint="Leave empty while you still pay it."
            />
          )}
        </div>
        <Input label="Note (optional)" maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={saving}>
            {cost ? 'Save' : 'Add cost'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
